import { Fragment, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Page } from '../theme/elements/Page';
import { SmallCard } from 'theme/elements/Cards';
import { OrangeCircle } from 'theme/elements/Chips';
import { WarningDialog } from 'components/WarningDialog';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getWebhookMonitoringAnalytics, getWebhookMonitoringHealthcheck } from 'services/webhook-monitoring.service';
import {
	clearError,
	clearTable,
	getWebhookMonitoringAnalyticsData,
	getWebhookMonitoringError,
	getWebhookMonitoringHealthState,
} from 'state/reducers/webhook-monitoring.reducer';
import {
	MonitoringFilters,
	MonitoringTable,
	QueueHealthSection,
	webhookMonitoringDashboardPanelSx,
} from 'sections/WebhookMonitoring';
import { pagesEnum } from 'constants/ui.constants';
import { WebhookMonitoringStatusBucket } from 'constants/webhook-monitoring.constants';

const statusSmallCardSx: CSSProperties = {
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	minWidth: 0,
	borderRadius: '10px',
	backgroundColor: '#fff',
	border: '2px solid #9ac0b1',
	boxShadow: 'inset 0 0 0 1px rgba(0, 127, 82, 0.12)',
	transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
	'&:hover': {
		borderColor: '#5e9a84',
		boxShadow: 'inset 0 0 0 1px rgba(0, 127, 82, 0.16), 0 4px 16px rgba(0, 58, 53, 0.1)',
		transform: 'translateY(-1px)',
	},
};

export const WebhookOperationsDashboardApp = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const analytics = useAppSelector(getWebhookMonitoringAnalyticsData);
	const health = useAppSelector(getWebhookMonitoringHealthState);
	const error = useAppSelector(getWebhookMonitoringError);
	const [filters, setFilters] = useState<Record<string, any>>({});

	useEffect(() => {
		dispatch(getWebhookMonitoringAnalytics());
		dispatch(getWebhookMonitoringHealthcheck());
	}, [dispatch]);

	const cards = useMemo(
		() => [
			{ key: WebhookMonitoringStatusBucket.Active, data: analytics.pendingAndInProgress },
			{ key: WebhookMonitoringStatusBucket.Completed, data: analytics.completedSuccessfully },
			{ key: WebhookMonitoringStatusBucket.DeadLetter, data: analytics.deadLetter },
		],
		[analytics.pendingAndInProgress, analytics.completedSuccessfully, analytics.deadLetter]
	);

	const basePath = `/${pagesEnum.WEBHOOK_MONITORING}`;
	const navigateToStatus = useCallback(
		(status: WebhookMonitoringStatusBucket) => {
			dispatch(clearTable());
			navigate(`${basePath}/webhook-operations/${status}`);
		},
		[basePath, dispatch, navigate]
	);

	return (
		<Fragment>
			<Page title={t('page.webhookMonitoring.dashboard.title')} loading={analytics.loading || health.loading}>
				<WarningDialog
					open={error.exist}
					subTitle={error.text ? t(error.text) : ''}
					handleCancel={() => dispatch(clearError())}
				/>
				<Stack spacing={4} useFlexGap>
					<QueueHealthSection />
					<Box sx={webhookMonitoringDashboardPanelSx}>
						<Typography variant="h4" sx={{ mb: 2 }}>
							{t('page.webhookMonitoring.dashboard.cards.title')}
						</Typography>
						<Grid container spacing={3}>
							{cards.map(({ key, data }) => (
								<Grid item xs={12} sm={6} md={4} key={key}>
									<Box sx={{ cursor: 'pointer', height: '100%' }}>
										<SmallCard
											styleObj={statusSmallCardSx}
											handleClick={() => navigateToStatus(key)}
										>
											<Stack>
												<Typography variant="boldBody3">{data.total}</Typography>
												<Stack direction="row" alignItems="center" spacing={1}>
													{data.today > 0 ? <OrangeCircle /> : null}
													<Typography
														variant="boldBody4"
														sx={{
															fontWeight: data.today > 0 ? 700 : 'auto',
															color: theme.palette.common.black,
														}}
													>
														{t(`page.webhookMonitoring.dashboard.card.${key}`)}
													</Typography>
												</Stack>
											</Stack>
											<img src="/icons/arrow-left.svg" style={{ marginRight: '10px' }} />
										</SmallCard>
									</Box>
								</Grid>
							))}
						</Grid>
					</Box>
					<MonitoringFilters filters={filters} setFilters={setFilters} includeWebhookStatus={true} />
					<MonitoringTable
						status={WebhookMonitoringStatusBucket.All}
						filters={filters}
						title={t('page.webhookMonitoring.table.all.title')}
						includeWebhookStatusColumn={true}
					/>
				</Stack>
			</Page>
		</Fragment>
	);
};
